import { Pog, PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import { cloneDeep, isEqual } from "es-toolkit";
import { PrimitiveAtom, useAtom } from "jotai";
import { useRef, useState } from "react";
import { useModals } from "src/components/Modals";

const comparable = (pog: Pog | null) => (pog ? { ...pog.toNonCircular(), metrics: null } : null);

/** Whether two planograms hold the same layout. Metrics are derived, so they are ignored. */
export const isSamePog = (a: Pog | null, b: Pog | null) => isEqual(comparable(a), comparable(b));

export const useModifiedPog = (pogAtom: PrimitiveAtom<Pog | null>) => {
  const { openConfirmModal } = useModals();
  const [pog, _setPog] = useAtom(pogAtom);
  const [originalPog, setOrignalPog] = useState<Pog | null>(null);
  /**
   * Copy of whatever setPogAsUnsaved last put on screen, so we can tell that
   * layout apart from one the user has since edited by hand.
   */
  const unsavedSource = useRef<Pog | null>(null);

  const setPog = (pog: PogNonCircular | null) => {
    unsavedSource.current = null;

    if (!pog) {
      _setPog(null);
      setOrignalPog(null);
      return;
    }
    setOrignalPog(cloneDeep(new Pog(pog)));
    _setPog(new Pog(pog));
  };

  /**
   * Replace the working planogram but keep the original, so the new layout
   * reads as an unsaved change: it shows as modified, Revert restores the saved
   * layout, and Save writes it. Used to put a proposed layout on screen without
   * pretending it has been saved.
   */
  const setPogAsUnsaved = (pog: PogNonCircular) => {
    const next = new Pog(pog);
    unsavedSource.current = cloneDeep(next);
    _setPog(next);
  };

  /**
   * Whether the planogram has been edited by hand since setPogAsUnsaved put one
   * on screen. False when nothing was put there, or when it is still untouched.
   *
   * This is a whole-planogram comparison, so call it when something is about to
   * overwrite the canvas rather than on every render.
   */
  const hasEditsSinceUnsavedPog = () =>
    unsavedSource.current !== null && !isSamePog(pog, unsavedSource.current);

  const revertPog = () => {
    openConfirmModal({
      title: "Revert modified planogram changes?",
      description: "All unsaved changes made to the planogram will be lost. This is irreversible.",
      buttons: [
        {
          id: "keep-changes",
          label: "Keep Changes",
          color: "primary",
          variant: "inverted",
        },
        {
          id: "revert-changes",
          label: "Revert Changes",
          color: "red",
          onClick: () => {
            unsavedSource.current = null;
            _setPog(originalPog ? cloneDeep(originalPog) : null);
          },
        },
      ],
    });
  };

  const savePogAsOriginal = (savedPog: PogNonCircular | null = pog?.toNonCircular() ?? null) => {
    if (!savedPog) return;

    unsavedSource.current = null;
    setOrignalPog(cloneDeep(new Pog(savedPog)));
  };

  /**
   * Applies a change the server already holds to both copies, so it neither reverts nor reads as unsaved.
   * A layout setPogAsUnsaved put on screen takes the change too: it is not a hand edit on that layout.
   */
  const patchPog = (update: (pog: PogNonCircular) => PogNonCircular) => {
    const patch = (current: Pog | null) => current && new Pog(update(current.toNonCircular()));
    _setPog(patch);
    setOrignalPog(patch);
    unsavedSource.current = patch(unsavedSource.current);
  };

  return {
    revertPog,
    setPog,
    setPogAsUnsaved,
    hasEditsSinceUnsavedPog,
    savePogAsOriginal,
    patchPog,
    pog,
    isModified: originalPog !== null && !isSamePog(pog, originalPog),
  };
};
